import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { LoggingService } from '../../services/shared/logging/logging.service';

@Directive({
  selector: '[appAutoFocusNext]',
  standalone: true,
})
export class AutoFocusNextDirective {
  @Input() skipValue?: number = undefined; // Allow configuring which value to skip; not skipping when undefined
  @Input() validateNumber: boolean = true; // Option to validate numbers only

  private lastProcessedValue: string = '';
  private lastProcessedTime: number = 0;

  constructor(private el: ElementRef<HTMLInputElement>, private loggingService: LoggingService) { }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const inputElement = this.el.nativeElement;
    const inputValue = inputElement.value?.trim();
    const currentTime = Date.now();

    // Skip if this is a programmatically dispatched event (not from user typing)
    if (event.isTrusted === false) {
      return;
    }

    if (!this.isValidInput(inputValue)) {
      return;
    }

    if (this.shouldSkipFocus(inputValue)) {
      return;
    }

    // For same values, check if enough time has passed since last processing
    // This handles the case where user types the same value again
    if (inputValue === this.lastProcessedValue) {
      if ((currentTime - this.lastProcessedTime) < 500) {
        return;
      }
    }

    // Update tracking
    this.lastProcessedValue = inputValue;
    this.lastProcessedTime = currentTime;

    // Let the component's focus management handle the complex logic
    // The directive only handles simple cases where the next input is immediately available
    this.focusNextElement();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Handle Enter key for immediate focus advancement
    if (event.key === 'Enter') {
      const inputElement = this.el.nativeElement;
      const currentValue = inputElement.value?.trim();

      if (this.isValidInput(currentValue) && !this.shouldSkipFocus(currentValue)) {
        event.preventDefault();
        this.lastProcessedValue = currentValue || '';
        this.lastProcessedTime = Date.now();
        this.focusNextElement();
      }
    }
  }

  private isValidInput(value: string): boolean {
    if (!value || value.length === 0) {
      return false;
    }

    // Allow Y/N inputs (single character)
    if (value.length === 1 && (value.toUpperCase() === 'Y' || value.toUpperCase() === 'N')) {
      return true;
    }

    // Allow numeric inputs (1-2 digits for golf scores)
    if (value.length <= 2 && (!this.validateNumber || !isNaN(Number(value)))) {
      return true;
    }

    return false;
  }

  private shouldSkipFocus(value: string): boolean {
    return Number(value) === this.skipValue;
  }

  private focusNextElement(): void {
    try {
      const currentTabIndex = Number(
        this.el.nativeElement.getAttribute('tabindex')
      );

      if (isNaN(currentTabIndex)) {
        return;
      }

      // Try to find the next enabled element within a reasonable range
      let nextTabIndex = currentTabIndex + 1;
      let attempts = 0;
      const maxAttempts = 50; // Reasonable limit to prevent infinite loops

      while (attempts < maxAttempts) {
        const nextElement = document.querySelector(
          `[tabindex="${nextTabIndex}"]`
        ) as HTMLElement;

        if (nextElement && this.isElementEnabled(nextElement)) {
          // Found an enabled element, focus it
          setTimeout(() => {
            nextElement.focus();
          }, 50); // Small delay to avoid conflicts with component focus management
          return;
        } if (!nextElement) {
          // No element with this tab index, try the next one
          nextTabIndex++;
          attempts++;
          continue;
        }

        // Element exists but is disabled, skip it
        nextTabIndex++;
        attempts++;
      }

      // If no simple next element found, trigger the component's focus management
      // Get the current input's hole number from data attributes
      const currentHoleNumber = parseInt(this.el.nativeElement.getAttribute('data-hole-number') || '0');
      const currentPlayerId = this.el.nativeElement.getAttribute('data-row-id');

      // Dispatch event with the hole information
      this.el.nativeElement.dispatchEvent(new CustomEvent('requestFocusAdvancement', {
        detail: {
          currentTabIndex: currentTabIndex,
          currentHoleNumber: currentHoleNumber,
          currentPlayerId: currentPlayerId
        },
        bubbles: true
      }));
    } catch (error) {
      this.loggingService.error(
        `AutoFocusNextDirective: Error focusing next element: ${error}`,
        'AutoFocusNextDirective'
      );
    }
  }  /**
   * Checks if an element is enabled (not disabled or readonly)
   * @param element The element to check
   * @returns True if the element is enabled, false otherwise
   */
  private isElementEnabled(element: HTMLElement): boolean {
    if (!(element instanceof HTMLInputElement)) {
      return false;
    }

    return !element.disabled &&
      !element.readOnly &&
      !element.hidden &&
      element.tabIndex !== -1 &&
      element.offsetParent !== null && // Check if element is visible
      element.style.display !== 'none' &&
      !element.hasAttribute('disabled') &&
      !element.hasAttribute('readonly') &&
      !element.classList.contains('readonly'); // Also check for readonly CSS class
  }
}
